import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download, Swords } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useResolveCrTagToSub } from '@/hooks/useResolveCrTagToSub';
import { useBattlesQuery, useProfileMeQuery, useCounterDeckMutation } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { AdvisorOverlay } from '@/components/hub/panels/decks/AdvisorOverlay';
import { CounterResult } from '@/components/hub/panels/decks/AdvisorResults';
import { PlayerSocialButtons } from '@/components/social/PlayerSocialButtons';
import { crDeckImportUrl } from '@/lib/crDeckLink';
import { cn } from '@/lib/cn';
import { resolvePov } from '@/lib/battlePov';
import type { CRBattle, CRBattlePlayer } from '@/types/qbot';

/**
 * State handed over by `<BattleRow>` when the user taps a row — lets the
 * detail render instantly without a refetch. Absent on a cold deep-link
 * (refresh / shared URL), in which case we refetch the 25-entry window and
 * index into it by `:idx`.
 */
interface BattleDetailState {
  battle?: CRBattle;
  playerTag?: string;
}

/** CR battleTime format: "20260520T214500.000Z" → Date (manual parse). */
function parseBattleTime(iso: string): Date | null {
  const m = iso.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return null;
  return new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`);
}

/**
 * One battle in full: result + crowns + trophy change, arena/mode/time, and
 * BOTH decks (yours + opponent) each with an in-game Import deep link. The
 * opponent's name links to `/cr/player/:tag`.
 *
 * The battles window is the same fixed 25-entry CR API window the list shows,
 * so `:idx` is an absolute index into `page=1, pageSize=25`. We prefer the
 * battle passed via router state (instant); the fetch is the deep-link
 * fallback only.
 */
export function CRBattleDetail() {
  const { t, i18n } = useTranslation('cr');
  const { idx } = useParams();
  const { state } = useLocation() as { state: BattleDetailState | null };

  const stateBattle = state?.battle;

  // Tag drives POV resolution and the fallback fetch. From router state when
  // available, else from /profile/me (skipped when state already has the tag).
  const { data: profile } = useProfileMeQuery(undefined, { skip: Boolean(state?.playerTag) });
  const playerTag =
    state?.playerTag ?? profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';

  const numericIdx = Number(idx);
  const { data, isFetching } = useBattlesQuery(
    { tag: playerTag, page: 1, pageSize: 25 },
    { skip: Boolean(stateBattle) || !playerTag },
  );

  // Counter the deck you just faced — deterministic qCrC advice built from your
  // own collection (no AI, no tier gate). Declared before the early returns
  // below so the hook order never changes.
  const [counterOpen, setCounterOpen] = useState(false);
  const [runCounter, counterState] = useCounterDeckMutation();

  const battle = stateBattle ?? data?.battles?.[numericIdx];

  if (!battle) {
    const loading = !stateBattle && (isFetching || !playerTag || !data);
    return (
      <div className="px-4 py-6 text-sm text-fg-muted">
        {loading ? t('battleDetail.loading') : t('battleDetail.notFound')}
      </div>
    );
  }

  const pov = resolvePov(battle, playerTag);
  if (!pov) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('battleDetail.notFound')}</div>;
  }
  const { me, opp, won, draw } = pov;

  const resultLabel = draw
    ? t('battleDetail.result.draw')
    : won
      ? t('battleDetail.result.won')
      : t('battleDetail.result.lost');
  const resultClass = draw
    ? 'bg-amber-500/20 text-amber-300'
    : won
      ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-rose-500/20 text-rose-300';

  const date = parseBattleTime(battle.battleTime);
  const timeLabel = date
    ? date.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : '';

  // Card NAMES, the shape the advisor endpoint takes (same call the Mazzi panel
  // makes for a meta deck). Fired lazily on first open, then cached by RTK.
  const counterOpponentDeck = () => {
    setCounterOpen(true);
    if (!counterState.data && !counterState.isLoading) {
      runCounter({ opponentDeck: opp.cards.map((c) => c.name) }).catch(() => undefined);
    }
  };

  return (
    // `relative` so the advisor overlay can cover the route stage instead of
    // scrolling away with the content behind it.
    <div className="relative h-full">
      <div className="flex h-full flex-col gap-2 overflow-y-auto overflow-x-hidden px-4 pb-3 pt-2">
        <header className="flex flex-col items-center gap-0.5 rounded-xl bg-bg-secondary px-3 py-2 text-center">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'rounded px-2 py-0.5 text-[10px] font-bold uppercase leading-tight tracking-wide',
                resultClass,
              )}
            >
              {resultLabel}
            </span>
            <span className="text-xl font-bold tabular-nums text-fg">
              {me.crowns}
              <span className="px-1 text-fg-muted">–</span>
              {opp.crowns}
            </span>
            {typeof me.trophyChange === 'number' && me.trophyChange !== 0 && (
              <span
                className={cn(
                  'text-[11px] font-semibold leading-tight tabular-nums',
                  me.trophyChange > 0 ? 'text-emerald-300' : 'text-rose-300',
                )}
              >
                {me.trophyChange > 0 ? '+' : ''}
                {me.trophyChange} 🏆
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 text-[10px] leading-tight text-fg-muted">
            <span>{battle.gameMode?.name ?? battle.type}</span>
            {battle.arena?.name && (
              <>
                <span aria-hidden>·</span>
                <span>{battle.arena.name}</span>
              </>
            )}
            {timeLabel && (
              <>
                <span aria-hidden>·</span>
                <span>{timeLabel}</span>
              </>
            )}
          </div>
        </header>

        <DeckSection heading={t('battleDetail.you')} player={me} />
        <DeckSection
          heading={t('battleDetail.opponent')}
          player={opp}
          viewProfileTo={opp.tag ? `/cr/player/${encodeURIComponent(opp.tag)}` : undefined}
          socialTag={opp.tag}
          onCounter={opp.cards?.length >= 8 ? counterOpponentDeck : undefined}
        />
      </div>

      <AdvisorOverlay
        open={counterOpen}
        title={t('panel.counter.title')}
        onClose={() => setCounterOpen(false)}
        loading={counterState.isLoading}
        error={counterState.error}
        onRetry={() =>
          runCounter({ opponentDeck: opp.cards.map((c) => c.name) }).catch(() => undefined)
        }
      >
        {counterState.data && <CounterResult result={counterState.data} />}
      </AdvisorOverlay>
    </div>
  );
}

interface DeckSectionProps {
  heading: string;
  player: CRBattlePlayer;
  /** When set, the player name links to this "View CR profile" route. */
  viewProfileTo?: string;
  /**
   * When set (opponent block), resolve this CR tag → Logto `sub` (QBot first,
   * qduels fallback) and, if the player is mapped, render follow/add-friend
   * buttons. Omitted for the "you" block.
   */
  socialTag?: string;
  /**
   * When set (opponent block, complete deck), show a "counter this deck" action.
   * The result itself is rendered by the route-level advisor overlay.
   */
  onCounter?: () => void;
}

function DeckSection({ heading, player, viewProfileTo, socialTag, onCounter }: DeckSectionProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();

  // Identity bridge (CR side): a battle opponent is known only by CR tag. We
  // resolve it to a Logto sub — QBot primary, qduels fallback. Only resolved
  // players get the social buttons; an unresolved tag yields `undefined` → no
  // buttons, and no further social API calls fire.
  const { logtoUserId: socialUserId } = useResolveCrTagToSub(socialTag);

  const handleImport = () => {
    haptics.impact('light');
    openLink.ifAvailable(crDeckImportUrl(player.cards));
  };

  return (
    // Stacked, not side-by-side: the deck is the subject of this screen, so it
    // takes the full width and the identity/actions sit above it. (An earlier
    // pass put them in a fixed left rail to save height — it squeezed the cards
    // too hard to read.)
    <section className="rounded-xl bg-bg-secondary p-2">
      <div className="mb-1.5 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase leading-tight tracking-wider text-fg-muted">
            {heading}
          </p>
          {viewProfileTo ? (
            <Link
              to={viewProfileTo}
              aria-label={t('battleDetail.viewProfile')}
              className="-my-1 flex items-center gap-0.5 py-1 text-sm font-semibold text-fg"
            >
              <span className="truncate">{player.name}</span>
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-brand" />
            </Link>
          ) : (
            <p className="truncate text-sm font-semibold text-fg">{player.name}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {onCounter && (
            <button
              type="button"
              onClick={() => {
                haptics.impact('light');
                onCounter();
              }}
              className="flex items-center gap-1 rounded-lg bg-bg-tertiary px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand transition-opacity active:opacity-80"
            >
              <Swords className="h-3.5 w-3.5" />
              {t('panel.action.counter')}
            </button>
          )}
          {player.cards?.length > 0 && (
            <button
              type="button"
              onClick={handleImport}
              className="flex items-center gap-1 rounded-lg bg-bg-tertiary px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand transition-opacity active:opacity-80"
            >
              <Download className="h-3.5 w-3.5" />
              {t('battleDetail.import')}
            </button>
          )}
        </div>
      </div>

      {player.cards?.length > 0 && <DeckGrid cards={player.cards} showLevels />}

      {socialUserId && (
        <div className="mt-2">
          <PlayerSocialButtons userId={socialUserId} interactive />
        </div>
      )}
    </section>
  );
}
